import React, { Component } from 'react';
import { Button } from 'antd';
import { ArrowLeft } from 'lucide-react';
import Inherit from '../../../core/inherit';
import ClientController from './client.controller';
import withNavigate from '../../../core/withNavigate';
import { Layout, CardSkeleton, AnalyticsSkeleton, RecentAudits, ShareOfShelf } from '../../common';
import { ClientTitle, StoresTable, MissingProducts, ShareData, ClientDetailAnalytics, ClientContractsSection, ClientCampaignsSection } from './elements';

/**
 * Client detail. Base page: a company overrides one section seam (`renderShareData`, …) in a
 * subclass registered as `pages.ClientProfile` in `companies/<id>/index.js`.
 */
export class ShelfClientProfile extends Inherit(ClientController, Component) {
    state = {
        client: {},
        gerente: {},
        asesores: [],
        loading: true,
        stores: [],
        filtered: [],
        recentAudits: [],
        elementsLoading: true,
        selectedStores: [],
        storeTypes: [],
        contracts: [],
        campaigns: []
    };

    /** Back link — hidden for client users who can't access the clients list. */
    renderBackLink() {
        if (this.isClientUser) return null;
        return (
            <Button
                type="text"
                size="small"
                icon={<ArrowLeft />}
                onClick={() => this.props.navigate('/dashboard/clients')}
                className="!-ml-2 mb-4 !text-surface-500 hover:!text-primary-600">
                Volver a Clientes
            </Button>
        );
    }

    renderLoading() {
        return (
            <div className="flex flex-col gap-y-4 animate-pulse">
                <CardSkeleton rows={2} />
                <AnalyticsSkeleton cards={3} />
                <CardSkeleton rows={8} />
            </div>
        );
    }

    /** Client header, analysis period and team. */
    renderHeader() {
        const { client, gerente, asesores } = this.state;
        return (
            <ClientTitle
                client={client}
                gerente={gerente}
                asesores={asesores}
                setLoading={this.setElementsLoading.bind(this)}
                getStoresChecksByClientId={this.fetchStoreChecks.bind(this)}
            />
        );
    }

    /** KPIs. */
    renderAnalytics() {
        const { client, stores, recentAudits } = this.state;
        return <ClientDetailAnalytics client={client} stores={stores} recentAudits={recentAudits} />;
    }

    renderStores() {
        const { stores, filtered, elementsLoading, recentAudits, selectedStores } = this.state;
        return (
            <StoresTable
                stores={stores}
                filtered={filtered}
                loading={elementsLoading}
                storeChecks={recentAudits}
                selectedStores={selectedStores}
                setFiltered={this.setFiltered.bind(this)}
                setStoreChecks={this.setRecentAudits.bind(this)}
                setSelectedStores={this.setSelectedStores.bind(this)}
            />
        );
    }

    renderRecentAudits() {
        const { recentAudits, elementsLoading } = this.state;
        return <RecentAudits storeChecks={recentAudits} loading={elementsLoading} />;
    }

    renderMissingProducts() {
        return <MissingProducts storeChecks={this.state.recentAudits} setStoreChecks={this.setRecentAudits.bind(this)} />;
    }

    /** Numeric distribution. */
    renderShareData() {
        const { recentAudits, storeTypes, client } = this.state;
        return <ShareData checks={recentAudits} storeTypes={storeTypes} channel={client?.channel} clientName={client?.name} />;
    }

    renderShareOfShelf() {
        const { recentAudits, client } = this.state;
        return <ShareOfShelf storeChecks={recentAudits} channel={client?.channel} isClientView />;
    }

    renderContracts() {
        return <ClientContractsSection contracts={this.state.contracts} />;
    }

    renderCampaigns() {
        return <ClientCampaignsSection campaigns={this.state.campaigns} />;
    }

    /** Everything after the loading gate, in order. */
    renderContent() {
        return (
            <div className="flex flex-col gap-y-4 animate-in">
                {this.renderHeader()}
                {this.renderAnalytics()}
                {this.renderStores()}
                {this.renderRecentAudits()}
                {this.renderMissingProducts()}
                {this.renderShareData()}
                {this.renderShareOfShelf()}
                {this.renderContracts()}
                {this.renderCampaigns()}
            </div>
        );
    }

    render() {
        return (
            <Layout current="clients">
                {this.renderBackLink()}
                {this.state.loading ? this.renderLoading() : this.renderContent()}
            </Layout>
        );
    }
}

export default withNavigate(ShelfClientProfile);
